import { WrapperAmountContainer, Row, Col, SeparatorTotal, TotalRow, ColPercent, HeaderContainer, Separator } from './WrapperAmount.styles';
import SafeHTML from '@/utils/safeHTML';
import { useAppSelector } from '@app/hooks';
import React, { useEffect, useState } from 'react';
import { formatNumber } from '@/utils/formatValues';
import { calculateTotalTradeLineBalance, calculatePercent, cashBack } from '@/utils/debtConsolidationCalculator';

interface WrapperAmountProps {
  barColor?: string;
  totalDrawColor?: string;
  isDrawAmountHidden?: boolean;
}

export const WrapperAmount: React.FC<WrapperAmountProps> = ({ isDrawAmountHidden = false }) => {
  const templateConfig = useAppSelector((state) => state.TemplateConfig);
  const loanApplication = useAppSelector((state) => state.LoanApplication);
  const { selectedLineAmount, selectedDrawAmount, includeCheckboxes } = useAppSelector((state) => state.VmoV2);
  const tradeLines = loanApplication?.borrowers[0]?.tradelines;
  const isHEL = loanApplication?.selectedProgramType === 'Home Equity Loan';

  const getLineAmountLabel = () => {
    if (isHEL) {
      return 'Loan Amount';
    }
    const hasDrawAmount = selectedDrawAmount > 0;
    return hasDrawAmount ? 'Line Amount Used' : 'Line Amount';
  };

  const getTotalDrawLabel = () => {
    if (isHEL) {
      return 'Loan Amount';
    }
    if (isDrawAmountHidden) {
      return 'Line Amount';
    }
    const hasDrawAmount = selectedDrawAmount > 0;
    return hasDrawAmount ? 'Total Draw Amount' : 'Line Amount';
  };

  const [totalUsed, setTotalUsed] = useState(calculateTotalTradeLineBalance(tradeLines || [], includeCheckboxes));

  const getTotalAmount = () => {
    if (isHEL) {
      const amount =
        Number(selectedLineAmount) ||
        Number(loanApplication.selectedProgram?.lineAmount) ||
        Number(loanApplication.selectedProgram?.loanAmount) ||
        Number(loanApplication.lineAmount) ||
        Number(loanApplication.initialLineAmount) ||
        0;
      return amount;
    }

    if (isDrawAmountHidden) {
      const amount =
        Number(selectedLineAmount) || Number(loanApplication.selectedProgram?.lineAmount) || Number(loanApplication.lineAmount) || Number(loanApplication.initialLineAmount) || 0;
      return amount;
    }

    const hasDrawAmount = selectedDrawAmount > 0 || loanApplication.drawAmount > 0;

    if (hasDrawAmount) {
      const drawAmount = Number(selectedDrawAmount) || Number(loanApplication.selectedProgram?.drawAmount) || Number(loanApplication.drawAmount) || 0;
      return drawAmount;
    } else {
      const amount =
        Number(selectedLineAmount) || Number(loanApplication.selectedProgram?.lineAmount) || Number(loanApplication.lineAmount) || Number(loanApplication.initialLineAmount) || 0;
      return amount;
    }
  };

  const totalAmount = getTotalAmount();
  const originationFee = loanApplication?.selectedProgram?.fees?.[0]?.feeAmount || 0;
  const isOriginationFeeEnabled = !(templateConfig?.pages?.vmo?.vmoV2?.debtConsolidation?.wrapperAmount?.originationFee?.isHidden ?? false);

  // Calculate percentage for the first row
  const [percent, setPercent] = useState(calculatePercent(totalUsed, totalAmount));

  useEffect(() => {
    const newTotalUsed = calculateTotalTradeLineBalance(tradeLines || [], includeCheckboxes);
    setTotalUsed(newTotalUsed);
    const newTotalAmount = getTotalAmount();
    setPercent(calculatePercent(newTotalUsed, newTotalAmount));
  }, [
    loanApplication.drawAmount,
    loanApplication.lineAmount,
    loanApplication.selectedProgram,
    tradeLines,
    loanApplication.selectedProgram.drawAmount,
    selectedLineAmount,
    selectedDrawAmount,
    includeCheckboxes,
  ]);

  const calculateCashBack = () => {
    let cashBackAmount = cashBack(loanApplication, totalUsed, selectedLineAmount, selectedDrawAmount, isDrawAmountHidden);
    if (isOriginationFeeEnabled && originationFee > 0) {
      cashBackAmount -= originationFee;
    }
    return cashBackAmount;
  };

  const getLineAmountText = () => {
    return getLineAmountLabel();
  };

  const getTotalDrawText = () => {
    return getTotalDrawLabel();
  };

  return (
    <WrapperAmountContainer data-testid="wrapper-amount" $isHidden={templateConfig?.pages?.vmo?.vmoV2?.debtConsolidation?.wrapperAmount?.isHidden ?? false}>
      <Row $isHidden={templateConfig?.pages?.vmo?.vmoV2?.debtConsolidation?.wrapperAmount?.title?.isHidden ?? false}>
        <Col>
          <HeaderContainer>
            <SafeHTML tag="h4" html={templateConfig?.pages?.vmo?.vmoV2?.debtConsolidation?.wrapperAmount?.title?.text || 'Loan Breakdown'} />
          </HeaderContainer>
        </Col>
      </Row>
      <Separator />
      <Row
        $isHidden={
          (templateConfig?.pages?.vmo?.vmoV2?.debtConsolidation?.wrapperAmount?.lineAmount?.isHidden ?? false) || (!isHEL && selectedDrawAmount > 0 && !isDrawAmountHidden)
        }
      >
        <Col>
          <SafeHTML tag="h4" html={getLineAmountText()} />
        </Col>
        <ColPercent>
          <p>{totalAmount > 0 ? formatNumber(totalAmount) : `${percent}%`}</p>
        </ColPercent>
      </Row>

      <Row
        $isHidden={
          isHEL
            ? true
            : isDrawAmountHidden
              ? true
              : selectedDrawAmount > 0
                ? false
                : (templateConfig?.pages?.vmo?.vmoV2?.debtConsolidation?.wrapperAmount?.totalDraw?.isHidden ?? false)
        }
      >
        <Col>
          <SafeHTML tag="span" html={getTotalDrawText()} />
        </Col>
        <Col>
          <p>{formatNumber(totalAmount)}</p>
        </Col>
      </Row>
      <Row $isHidden={templateConfig?.pages?.vmo?.vmoV2?.debtConsolidation?.wrapperAmount?.debtsBeingPaid?.isHidden ?? false}>
        <Col>
          <SafeHTML tag="span" html={templateConfig?.pages?.vmo?.vmoV2?.debtConsolidation?.wrapperAmount?.debtsBeingPaid?.text || 'Debts Being Paid'} />
        </Col>
        <Col>
          <p>{formatNumber(totalUsed)}</p>
        </Col>
      </Row>
      <Row $isHidden={templateConfig?.pages?.vmo?.vmoV2?.debtConsolidation?.wrapperAmount?.originationFee?.isHidden ?? false}>
        <Col>
          <SafeHTML tag="span" html={templateConfig?.pages?.vmo?.vmoV2?.debtConsolidation?.wrapperAmount?.originationFee?.text || 'Origination Fee'} />
        </Col>
        <Col>
          <p>{formatNumber(originationFee)}</p>
        </Col>
      </Row>
      <SeparatorTotal $totalDrawColor={'currentColor'} />
      <TotalRow $totalDrawColor={'currentColor'} $isHidden={templateConfig?.pages?.vmo?.vmoV2?.debtConsolidation?.wrapperAmount?.cashBack?.isHidden ?? false}>
        <Col>
          <SafeHTML tag="span" html={templateConfig?.pages?.vmo?.vmoV2?.debtConsolidation?.wrapperAmount?.cashBack?.text || 'Cash back to you'} />
        </Col>
        <Col>
          <p>{formatNumber(calculateCashBack())}</p>
        </Col>
      </TotalRow>
    </WrapperAmountContainer>
  );
};
